/*!
 * Copyright © 2026. Verizon Connect Ireland Limited. All rights reserved.
 */

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable, Injector, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';

import { APP_CONFIG } from '../../config/app-config.token';
import { AccountFeatureToggleName } from '../../constants/account-feature-toggle.constant';

interface ToggleResultDto {
  name: string;
  value: boolean;
}

/**
 * Resolves account feature toggles through the Toggle API. Each toggle is fetched at most once per
 * app lifetime and cached per name; an outage or absent name fails closed to `false`.
 */
@Injectable({ providedIn: 'root' })
export class AccountFeatureToggleService {
  private readonly _http = inject(HttpClient);
  private readonly _toggleUrl = `${inject(APP_CONFIG).helpPanel.featureToggleUrl}/v1/toggles`;
  private readonly _injector = inject(Injector);

  private readonly _streams = new Map<AccountFeatureToggleName, Observable<boolean>>();
  private readonly _signals = new Map<AccountFeatureToggleName, Signal<boolean>>();

  isEnabled(name: AccountFeatureToggleName): Signal<boolean> {
    const cached = this._signals.get(name);
    if (cached) {
      return cached;
    }
    const signal = toSignal(this._toggle$(name), { initialValue: false, injector: this._injector });
    this._signals.set(name, signal);
    return signal;
  }

  private _toggle$(name: AccountFeatureToggleName): Observable<boolean> {
    const cached = this._streams.get(name);
    if (cached) {
      return cached;
    }
    const stream = this._fetchToggle(name).pipe(shareReplay({ bufferSize: 1, refCount: false }));
    this._streams.set(name, stream);
    return stream;
  }

  private _fetchToggle(name: AccountFeatureToggleName): Observable<boolean> {
    return this._http
      .post<ToggleResultDto[]>(this._toggleUrl, { toggles: [name], application: null }, {
        headers: new HttpHeaders({ 'Correlation-Id': this._newCorrelationId() })
      })
      .pipe(
        map(results => results?.find(result => result.name === name)?.value ?? false),
        catchError(() => of(false))
      );
  }

  private _newCorrelationId(): string {
    return typeof crypto?.randomUUID === 'function' ? crypto.randomUUID() : `${Date.now()}`;
  }
}
